/**
 * Color Categorization Helper
 * Maps individual colors to their parent categories
 */
if (typeof ColorCategorizer === 'undefined') {
class ColorCategorizer {
  constructor() {
    this.colorCategories = null;
    this.colorToCategory = new Map();
    this.loadColorCategories();
  }

  async loadColorCategories() {
    try {
      const response = await fetch(window.colorCategoriesUrl || '/assets/color-categories.json');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      this.colorCategories = await response.json();
      this.buildColorMap();
    } catch (error) {
      console.warn('Failed to load color categories from JSON, using fallback data:', error);
      // Fallback to inline data if JSON fails to load
      this.colorCategories = {
        "Neutrals": ["White", "Off White", "Ivory", "Cream", "Beige", "Sand", "Taupe", "Light Gray", "Gray", "Dark Gray", "Charcoal", "Black"],
        "Browns": ["Light Brown", "Tan", "Khaki", "Camel", "Walnut", "Oak", "Maple", "Mahogany", "Chestnut", "Espresso", "Chocolate", "Cognac", "Dark Brown", "Brown"],
        "Reds & Oranges": ["Light Pink", "Blush", "Pink", "Fuchsia", "Magenta", "Coral", "Peach", "Orange", "Terracotta", "Rust", "Red", "Burgundy", "Wine"],
        "Yellows & Golds": ["Light Yellow", "Pastel Yellow", "Yellow", "Mustard", "Golden Yellow", "Ochre", "Gold", "Champagne", "Brass", "Bronze"],
        "Greens": ["Mint", "Sage", "Celadon", "Olive", "Khaki Green", "Moss", "Forest Green", "Emerald", "Teal", "Seafoam", "Dark Green"],
        "Blues": ["Light Blue", "Baby Blue", "Sky Blue", "Turquoise", "Aqua", "Cyan", "Royal Blue", "Blue", "Navy Blue", "Indigo", "Midnight Blue"],
        "Purples & Violets": ["Lavender", "Lilac", "Mauve", "Violet", "Purple", "Plum", "Eggplant", "Orchid"],
        "Metallics": ["Silver", "Chrome", "Steel", "Pewter", "Aluminum", "Gold", "Brass", "Bronze", "Copper"],
        "Stones & Neutrals Extended": ["Concrete Gray", "Stone", "Slate", "Granite", "Marble White", "Marble Black", "Sandstone", "Terracotta Clay"],
        "Special Finishes": ["Transparent", "Frosted", "Clear", "Matte", "Glossy", "Mirror"]
      };
      this.buildColorMap();
    }
  }

  buildColorMap() {
    for (const [category, colors] of Object.entries(this.colorCategories)) {
      colors.forEach(color => {
        const normalizedColor = color.toLowerCase().trim();
        this.colorToCategory.set(normalizedColor, category);
        
        // Also add without spaces and special characters for better matching
        const cleanColor = normalizedColor.replace(/[^\w]/g, '');
        if (cleanColor !== normalizedColor) {
          this.colorToCategory.set(cleanColor, category);
        }
      });
    }
  }

  getCategory(color) {
    const colorLower = color.toLowerCase().trim();
    
    // Direct match first
    if (this.colorToCategory.has(colorLower)) {
      return this.colorToCategory.get(colorLower);
    }
    
    // Try without special characters
    const cleanColor = colorLower.replace(/[^\w]/g, '');
    if (this.colorToCategory.has(cleanColor)) {
      return this.colorToCategory.get(cleanColor);
    }
    
    // Try partial matching for compound colors
    for (const [mappedColor, category] of this.colorToCategory.entries()) {
      if (colorLower.includes(mappedColor) || mappedColor.includes(colorLower)) {
        return category;
      }
    }
    
    // If still no match, try keyword-based matching
    const colorKeywords = {
      'Neutrals': ['white', 'black', 'gray', 'grey', 'cream', 'beige', 'ivory', 'neutral'],
      'Browns': ['brown', 'tan', 'khaki', 'camel', 'walnut', 'oak', 'mahogany', 'espresso', 'chocolate'],
      'Reds & Oranges': ['red', 'pink', 'coral', 'orange', 'peach', 'rust', 'burgundy', 'wine', 'magenta'],
      'Yellows & Golds': ['yellow', 'gold', 'mustard', 'brass', 'bronze', 'champagne'],
      'Greens': ['green', 'mint', 'sage', 'olive', 'moss', 'emerald', 'teal'],
      'Blues': ['blue', 'navy', 'turquoise', 'aqua', 'cyan', 'indigo'],
      'Purples & Violets': ['purple', 'violet', 'lavender', 'lilac', 'mauve', 'plum', 'orchid'],
      'Metallics': ['silver', 'chrome', 'steel', 'aluminum', 'copper', 'metallic'],
      'Special Finishes': ['transparent', 'clear', 'frosted', 'matte', 'glossy', 'mirror']
    };
    
    for (const [category, keywords] of Object.entries(colorKeywords)) {
      if (keywords.some(keyword => colorLower.includes(keyword))) {
        return category;
      }
    }
    
    return 'Other';
  }

  getAllCategories() {
    return Object.keys(this.colorCategories || {});
  }

  getColorsInCategory(category) {
    return this.colorCategories?.[category] || [];
  }

  /**
   * Transform color items to show hierarchical structure
   * @param {Array} items - Algolia color facet items
   * @returns {Array} - Hierarchical color structure
   */
  transformColorItems(items) {
    const categoryGroups = {};
    
    // Group colors by category
    items.forEach(item => {
      const category = this.getCategory(item.label);
      if (!categoryGroups[category]) {
        categoryGroups[category] = {
          label: category,
          value: category,
          count: 0,
          colors: [],
          isCategory: true
        };
      }
      
      categoryGroups[category].count += item.count;
      categoryGroups[category].colors.push({
        ...item,
        category: category,
        isSubColor: true
      });
    });

    // Convert to hierarchical structure with proper ordering
    const hierarchicalItems = [];
    
    // Define category order (put "Other" at the end)
    const categoryOrder = [
      'Neutrals', 'Browns', 'Reds & Oranges', 'Yellows & Golds', 
      'Greens', 'Blues', 'Purples & Violets', 'Metallics', 
      'Stones & Neutrals Extended', 'Special Finishes', 'Other'
    ];
    
    // Sort categories by predefined order
    const sortedCategories = Object.values(categoryGroups)
      .sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a.label);
        const bIndex = categoryOrder.indexOf(b.label);
        const aPos = aIndex === -1 ? categoryOrder.length : aIndex;
        const bPos = bIndex === -1 ? categoryOrder.length : bIndex;
        return aPos - bPos;
      });
    
    sortedCategories.forEach(categoryGroup => {
      // Only show categories that have colors
      if (categoryGroup.colors.length > 0) {
        // Add category header
        hierarchicalItems.push(categoryGroup);
        
        // Add colors under category
        categoryGroup.colors
          .sort((a, b) => b.count - a.count) // Sort by count descending
          .forEach(color => {
            hierarchicalItems.push(color);
          });
      }
    });

    return hierarchicalItems;
  }
}

// Global instance
if (!window.colorCategorizer) {
  window.colorCategorizer = new ColorCategorizer();
}
}
